import { create } from 'zustand';

/**
 * Shared selection state for the engine-agnostic document workspace.
 *
 * This store is the single binding contract between the right-hand Q&A index
 * panel and the left-hand editor host. The Q&A panel writes the active
 * question on row click ("click-to-jump"); a real document editor (docx-editor
 * for Word, Univer for Excel — mounted in a later task) can both subscribe to
 * `activeQuestionId` to scroll/highlight the matching anchor and call
 * `setActiveQuestion` when the cursor lands inside an answer region, keeping
 * the two panes in sync.
 *
 * `activeAnchor` is the optional document coordinate for the active question
 * (e.g. a paragraph id or cell address). It is intentionally untyped beyond a
 * string so either engine can drive it; the panel sets it from the question's
 * anchor when known.
 */
interface WorkspaceState {
  /** The currently selected question id, or null when nothing is selected. */
  activeQuestionId: string | null;

  /** The active question's document anchor, if known (paragraph id / cell ref). */
  activeAnchor: string | null;

  /**
   * Select a question (the panel calls this on row click; an editor may call
   * it when the cursor enters an answer region). Pass an anchor to keep the
   * editor host aligned with the selection.
   */
  setActiveQuestion: (questionId: string | null, anchor?: string | null) => void;

  /** Clear the current selection. */
  clearActiveQuestion: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>((set) => ({
  activeQuestionId: null,
  activeAnchor: null,

  setActiveQuestion: (activeQuestionId, anchor = null) =>
    set({ activeQuestionId, activeAnchor: anchor }),

  clearActiveQuestion: () => set({ activeQuestionId: null, activeAnchor: null }),
}));
